import * as React from 'react';
import { Radio } from './Radio';
import type { RadioSize } from './Radio';
import { useGroupState, GroupFieldset } from '../shared';
import type { GroupOption } from '../shared';

/**
 * Option type for RadioGroup
 */
export type RadioGroupOption = GroupOption;

/**
 * RadioGroup component props
 */
export interface RadioGroupProps {
  /**
   * Name attribute for the radio inputs (required for radio group behavior)
   */
  name: string;
  /**
   * Label for the radio group
   */
  label?: string;
  /**
   * Array of options to display
   */
  options: RadioGroupOption[];
  /**
   * Currently selected value (controlled mode)
   */
  value?: string;
  /**
   * Default selected value (uncontrolled mode)
   */
  defaultValue?: string;
  /**
   * Callback when selection changes
   */
  onChange?: (value: string) => void;
  /**
   * Layout orientation of the radios
   * @default 'vertical'
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Size variant of the radios
   * @default 'md'
   */
  size?: RadioSize;
  /**
   * Whether the group is in an error state
   */
  error?: boolean;
  /**
   * Error message to display
   */
  errorMessage?: string;
  /**
   * Whether the group is required
   */
  required?: boolean;
  /**
   * Whether all radios in the group are disabled
   */
  disabled?: boolean;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Helper text displayed below the group
   */
  helperText?: string;
  /**
   * ID for the group (used for accessibility)
   */
  id?: string;
}

/**
 * RadioGroup component for selecting a single option from multiple choices.
 *
 * Radio buttons in a group share the same name and only one can be selected at a time.
 * Supports keyboard navigation with arrow keys and space to select.
 *
 * @example
 * // Basic vertical group
 * <RadioGroup
 *   name="payment"
 *   label="Payment Method"
 *   options={[
 *     { value: 'credit', label: 'Credit Card' },
 *     { value: 'debit', label: 'Debit Card' },
 *     { value: 'bank', label: 'Bank Transfer' },
 *   ]}
 *   value={selectedValue}
 *   onChange={setSelectedValue}
 * />
 *
 * @example
 * // Horizontal group with error
 * <RadioGroup
 *   name="coverage"
 *   label="Coverage Type"
 *   options={options}
 *   orientation="horizontal"
 *   error
 *   errorMessage="Please select a coverage type"
 * />
 */
const RadioGroup = React.forwardRef<HTMLFieldSetElement, RadioGroupProps>(
  (
    {
      name,
      label,
      options,
      value: controlledValue,
      defaultValue,
      onChange,
      orientation = 'vertical',
      size = 'md',
      error = false,
      errorMessage,
      required = false,
      disabled = false,
      className = '',
      helperText,
      id: providedId,
    },
    ref
  ) => {
    const {
      groupId, currentValue: selectedValue, setValue,
      showError, message, messageId,
    } = useGroupState<string | undefined, string>({
      prefix: 'radio-group', providedId, controlledValue, defaultValue,
      onChange, error, errorMessage, helperText,
    });

    return (
      <GroupFieldset
        ref={ref}
        groupId={groupId}
        className={className}
        messageId={messageId}
        error={error}
        label={label}
        required={required}
        orientation={orientation}
        message={message}
        showError={showError}
        optionsContainerProps={{
          role: 'radiogroup',
          'aria-labelledby': label ? groupId : undefined,
        }}
      >
        {options.map((option) => (
          <Radio
            key={option.value}
            name={name}
            value={option.value}
            label={option.label}
            checked={selectedValue === option.value}
            onChange={() => { setValue(option.value); }}
            disabled={disabled || option.disabled}
            error={error}
            size={size}
          />
        ))}
      </GroupFieldset>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export { RadioGroup };
